/**
 * GoodGuardian — Ask Anything Screen
 * Unified AI interface powered by the Orchestrator.
 * Automatically routes each message to the right specialist agent.
 */
import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import AppHeader from '@/components/layout/AppHeader'
import MarkdownText from '@/components/common/MarkdownText'
import { callOrchestrator } from '@/utils/ai'
import { useApp } from '@/context/AppContext'
import { PREFILL_KEY } from '@/utils/agentHandoff'
import VoiceAgent from '@/components/VoiceAgent'

// ── Agent badge config ─────────────────────────────────────────────────────────
const AGENT_META = {
  'chat':             { label: 'Legal Chat',       icon: '💬', color: 'bg-blue-100 text-blue-700' },
  'case-analyst':     { label: 'Case Analyst',     icon: '🔍', color: 'bg-purple-100 text-purple-700' },
  'document-drafter': { label: 'Document Drafter', icon: '📄', color: 'bg-teal-100 text-teal-700' },
  'cps-advisor':      { label: 'CPS Advisor',      icon: '🛡️', color: 'bg-orange-100 text-orange-700' },
  'client-coach':     { label: 'Court Coach',      icon: '⚖️', color: 'bg-indigo-100 text-indigo-700' },
  'researcher':       { label: 'Legal Researcher', icon: '📚', color: 'bg-green-100 text-green-700' },
  'emergency':        { label: 'Emergency',         icon: '🚨', color: 'bg-red-100 text-red-700' },
}

const QUICK_PROMPTS = [
  { text: 'What are my custody rights?', icon: '⚖️' },
  { text: 'CPS just contacted me — what do I do?', icon: '🛡️' },
  { text: 'Draft a parenting plan for me', icon: '📄' },
  { text: 'What should I expect at my hearing?', icon: '🏛️' },
  { text: 'How do I modify a custody order?', icon: '🔄' },
  { text: 'What does "best interests of the child" mean?', icon: '👶' },
]

function AgentBadge({ agent }) {
  const meta = AGENT_META[agent] || AGENT_META['chat']
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
      <span>{meta.icon}</span>
      {meta.label}
    </span>
  )
}

function Message({ msg }) {
  const isUser = msg.role === 'user'
  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} gap-1`}>
      {/* Agent badge on AI messages */}
      {!isUser && msg.agent && (
        <div className="flex items-center gap-1.5 px-1">
          <AgentBadge agent={msg.agent} />
          {msg.confidence != null && (
            <span className="text-sm text-slate-400">
              {Math.round(msg.confidence * 100)}% match
            </span>
          )}
        </div>
      )}

      <div className={isUser ? 'bubble-user' : 'bubble-ai'}>
        <MarkdownText content={msg.content} isUser={isUser} />
      </div>

      {/* Citations */}
      {!isUser && msg.citations?.length > 0 && (
        <div className="px-1 space-y-0.5">
          {msg.citations.map((c, i) => (
            <p key={i} className="text-sm text-slate-400">
              📖 {typeof c === 'string' ? c : c.source || c.title || JSON.stringify(c)}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

export default function AskAnything() {
  const { user } = useApp()
  const navigate = useNavigate()
  const location = useLocation()
  const [messages, setMessages] = useState([])
  const [input, setInput]       = useState('')
  const [loading, setLoading]   = useState(false)
  const [routingHint, setRoutingHint] = useState('')
  const bottomRef  = useRef(null)
  const textareaRef = useRef(null)

  // Pre-fill from agent handoff
  useEffect(() => {
    const prefill = location.state?.[PREFILL_KEY]
    if (prefill) {
      setInput(prefill)
      textareaRef.current?.focus()
      window.history.replaceState({}, '')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
  }, [input])

  const send = async (text) => {
    const message = (text || input).trim()
    if (!message || loading) return

    const userMsg = { role: 'user', content: message }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setLoading(true)
    setRoutingHint('Analyzing your question…')

    try {
      const history = messages.map(m => ({
        role: m.role,
        content: m.content,
      }))

      const result = await callOrchestrator({
        message,
        history,
        user: user || {},
        state: user?.state,
        now:      new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })

      setRoutingHint('')
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: result.reply,
        agent: result.routed_to,
        confidence: result.confidence,
        citations: result.citations || [],
      }])

      // If case analyst returned structured data, offer navigation
      if (result.routed_to === 'case-analyst' && result.assessment) {
        // Could navigate to CaseAnalyst screen with pre-loaded data in future
      }

      // If document drafter returned a doc, offer to view it
      if (result.routed_to === 'document-drafter' && result.document) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '📄 Your document draft has been generated. Go to **Documents** in the menu to find, edit, and save it.',
          agent: 'document-drafter',
          confidence: null,
          citations: [],
        }])
      }

    } catch (err) {
      setRoutingHint('')
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `⚠️ Something went wrong: ${err.message || 'Please try again.'}`,
        agent: 'chat',
        confidence: null,
        citations: [],
      }])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const isEmpty = messages.length === 0

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Ask Anything" showBack />

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">

        {/* Empty state */}
        {isEmpty && (
          <div className="flex flex-col items-center pt-6 pb-2 text-center">
            <div className="w-16 h-16 brand-gradient rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800">Ask me anything</h2>
            <p className="text-base text-slate-500 mt-1 max-w-xs">
              I'll automatically route your question to the right specialist — legal research, documents, CPS, court prep, and more.
            </p>

            {/* Agent chips */}
            <div className="flex flex-wrap justify-center gap-1.5 mt-4">
              {Object.values(AGENT_META).filter(a => a.label !== 'Emergency').map(a => (
                <span key={a.label} className={`text-xs px-2 py-1 rounded-full font-medium ${a.color}`}>
                  {a.icon} {a.label}
                </span>
              ))}
            </div>

            {/* Quick prompts */}
            <div className="w-full mt-5 space-y-2">
              {QUICK_PROMPTS.map((q) => (
                <button
                  key={q.text}
                  onClick={() => send(q.text)}
                  className="w-full text-left px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-base text-slate-700 hover:bg-slate-100 active:scale-[0.98] transition-all flex items-center gap-2"
                >
                  <span className="text-base flex-shrink-0">{q.icon}</span>
                  <span>{q.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, i) => (
          <Message key={i} msg={msg} />
        ))}

        {/* Typing / routing indicator */}
        {loading && (
          <div className="flex flex-col items-start gap-1">
            {routingHint && (
              <span className="text-sm text-slate-400 px-1">{routingHint}</span>
            )}
            <div className="bubble-ai">
              <div className="flex gap-1 items-center h-5">
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div className="border-t border-slate-100 bg-white px-4 py-3 safe-bottom">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            className="flex-1 border border-slate-300 rounded-2xl px-4 py-3 text-base text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 no-scrollbar leading-relaxed"
            placeholder="Ask any family law question…"
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <VoiceAgent
            agent="orchestrate"
            onTranscript={(text, role) => {
              setMessages(m => [...m, { role: role === 'user' ? 'user' : 'assistant', content: text, ts: Date.now() }])
            }}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || loading}
            className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center flex-shrink-0 disabled:opacity-40 active:scale-95 transition-all shadow-sm"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <p className="text-center text-sm text-slate-400 mt-2">
          Legal education only · Not legal advice · Attorney review recommended
        </p>
      </div>
    </div>
  )
}
